import React from "react";
import { PlusIcon, TrashIcon } from "@heroicons/react/24/outline";
import { newRequirementId } from "../../../utils/jobOpeningRequirements";

export function RequirementItemsEditor({
  items = [],
  onChange,
  label,
  hint,
  addLabel,
}) {
  const rows = items.length ? items : [{ id: newRequirementId(), text: "" }];

  const update = (id, text) => {
    onChange(rows.map((item) => (item.id === id ? { ...item, text } : item)));
  };

  const remove = (id) => {
    const next = rows.filter((item) => item.id !== id);
    onChange(next.length ? next : [{ id: newRequirementId(), text: "" }]);
  };

  const add = () => {
    onChange([...rows, { id: newRequirementId(), text: "" }]);
  };

  return (
    <div className="space-y-2">
      {label ? (
        <div>
          <label className="mb-1 block text-sm font-semibold text-slate-700">
            {label}
          </label>
          {hint ? <p className="text-xs text-slate-500">{hint}</p> : null}
        </div>
      ) : null}
      <ul className="space-y-2">
        {rows.map((item, index) => (
          <li key={item.id} className="flex items-center gap-2">
            <span className="w-6 shrink-0 text-center text-xs font-semibold text-slate-400">
              {index + 1}
            </span>
            <input
              type="text"
              value={item.text}
              onChange={(e) => update(item.id, e.target.value)}
              className="w-full rounded-xl border border-slate-200 px-3 py-2.5 text-sm"
            />
            <button
              type="button"
              onClick={() => remove(item.id)}
              className="rounded-lg p-2 text-slate-400 hover:bg-slate-100 hover:text-rose-600"
              aria-label="remove"
            >
              <TrashIcon className="h-4 w-4" />
            </button>
          </li>
        ))}
      </ul>
      <button
        type="button"
        onClick={add}
        className="inline-flex items-center gap-1.5 text-sm font-semibold text-[#1463C7]"
      >
        <PlusIcon className="h-4 w-4" />
        {addLabel}
      </button>
    </div>
  );
}

export function RequirementChecksEditor({
  items = [],
  onChange,
  label,
  hint,
  addLabel,
  editable = false,
  emptyLabel,
}) {
  const update = (id, patch) => {
    onChange(items.map((item) => (item.id === id ? { ...item, ...patch } : item)));
  };

  const remove = (id) => {
    onChange(items.filter((item) => item.id !== id));
  };

  const add = () => {
    onChange([
      ...items,
      { id: newRequirementId(), text: "", checked: false },
    ]);
  };

  if (!items.length && !editable) {
    return emptyLabel ? (
      <p className="text-sm text-slate-500">{emptyLabel}</p>
    ) : null;
  }

  return (
    <div className="space-y-2">
      {label ? (
        <div>
          <label className="mb-1 block text-sm font-semibold text-slate-700">
            {label}
          </label>
          {hint ? <p className="text-xs text-slate-500">{hint}</p> : null}
        </div>
      ) : null}
      <ul className="space-y-2">
        {items.map((item) => (
          <li
            key={item.id}
            className="flex items-start gap-3 rounded-xl border border-slate-200 px-3 py-2.5"
          >
            <input
              type="checkbox"
              className="mt-1 h-4 w-4 rounded border-slate-300 text-[#1463C7] disabled:cursor-not-allowed disabled:opacity-70"
              checked={!!item.checked}
              onChange={(e) => update(item.id, { checked: e.target.checked })}
              disabled={!onChange}
            />
            {editable ? (
              <input
                type="text"
                value={item.text}
                onChange={(e) => update(item.id, { text: e.target.value })}
                className="w-full rounded-lg border border-slate-200 px-3 py-1.5 text-sm"
              />
            ) : (
              <span className="text-sm leading-6 text-slate-800">{item.text}</span>
            )}
            {editable ? (
              <button
                type="button"
                onClick={() => remove(item.id)}
                className="rounded-lg p-1.5 text-slate-400 hover:bg-slate-100 hover:text-rose-600"
                aria-label="remove"
              >
                <TrashIcon className="h-4 w-4" />
              </button>
            ) : null}
          </li>
        ))}
      </ul>
      {editable ? (
        <button
          type="button"
          onClick={add}
          className="inline-flex items-center gap-1.5 text-sm font-semibold text-[#1463C7]"
        >
          <PlusIcon className="h-4 w-4" />
          {addLabel}
        </button>
      ) : null}
    </div>
  );
}

export function RequirementItemsList({ items = [], emptyLabel }) {
  if (!items.length) {
    return <p className="text-sm text-slate-500">{emptyLabel}</p>;
  }

  return (
    <ul className="space-y-2">
      {items.map((item) => (
        <li key={item.id || item.text} className="flex items-start gap-3 text-sm">
          <span className="mt-1.5 h-2 w-2 shrink-0 rounded-full bg-[#1463C7]" />
          <span className="leading-6 text-slate-800">{item.text}</span>
        </li>
      ))}
    </ul>
  );
}
